import { useContext } from "react";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import {
  SUN_STATE,
  VIEW_TYPES,
  STREAM_TYPES,
  INTERIOR_CAMERA_NAMES,
  EXTERIOR_CAMERA_NAMES,
  INTERIOR_CAMERA_LABELS,
  EXTERIOR_CAMERA_LABELS,
  CAMERA_PREFIX_NAMES,
  MODELS,
  ANIMATIONS,
  LAMPS_OFF_VARIANT_EXT,
  MODELS_NAMES,
} from "shared/enum";
import useVariant from "hooks/useVariant";
import useGtagVariantData from "hooks/useGtagVariantData";
import { isDarkTheme } from "helpers/darkTheme";
import getEnvLoadLevelsObject from "helpers/getEnvLoadLevelsObject";
import { getWasmMethods } from "@3ds/3DEXCITEshow-streaming";

import { useMediaQuery, type Theme, type TypographyProps } from "@mui/material";
import type { ViewTypes, CameraViewType, StreamTypes } from "types/app";

type ViewSelectorProps = {
  isMobile: boolean;
  streamViewType: StreamTypes;
  selected: ViewTypes;
  onChange: (option: ViewTypes) => void;
  isDraweOpenOnLg: boolean;
  isDrawerOpenOnMd?: boolean;
};

function ViewSelector({
  isMobile,
  streamViewType,
  selected,
  onChange,
  isDraweOpenOnLg,
  isDrawerOpenOnMd,
}: ViewSelectorProps) {
  const theme = useTheme();
  const isSmallport = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack direction="row" columnGap={{ xs: 0.5, sm: 1.5, md: 2 }}>
      <ViewChangeTitle
        variant="collapsible"
        isMobile={isMobile}
        selected={selected === VIEW_TYPES.INTERIOR}
        onClick={() => onChange(VIEW_TYPES.INTERIOR)}
        disabled={streamViewType === STREAM_TYPES.IN_GLO}
        isDraweOpenOnLg={isDraweOpenOnLg}
        isDrawerOpenOnMd={isDrawerOpenOnMd}
      >
        {isSmallport ? "INT" : "Interior"}
      </ViewChangeTitle>

      <ViewChangeTitle
        variant="collapsible"
        isMobile={isMobile}
        selected={selected === VIEW_TYPES.EXTERIOR}
        onClick={() => onChange(VIEW_TYPES.EXTERIOR)}
        disabled={streamViewType === STREAM_TYPES.IN_GLO}
        isDraweOpenOnLg={isDraweOpenOnLg}
        isDrawerOpenOnMd={isDrawerOpenOnMd}
      >
        {isSmallport ? "EXT" : "Exterior"}
      </ViewChangeTitle>
    </Stack>
  );
}

function ModelSwitch({
  handleStream,
  isMobile,
  closeDrawer,
  handleLoadScreen,
  isDraweOpenOnLg,
  isDrawerOpenOnMd,
}: {
  handleStream: (shouldStream: boolean) => void;
  isMobile: boolean;
  closeDrawer: VoidFunction;
  handleLoadScreen: (val: boolean) => void;
  isDraweOpenOnLg: boolean;
  isDrawerOpenOnMd?: boolean;
}) {
  const wasmMethods = getWasmMethods();
  const variant = useVariant();
  const getGtagData = useGtagVariantData();
  const isBE6 = variant.code === MODELS["BE 6"].code;

  const { setCachedState, configuration } = useContext(DataContext);
  const {
    demoState,
    setDemoState,
    cameras,
    turntableState,
    scenarios,
    streamViewType,
    setStreamingState,
    setStreamViewType,
    setTurntableState,
    checkSessionAvailability,
  } = useContext(StreamContext);

  const { isTurntable } = turntableState;
  const exteriorMainCamera = cameras?.find((cam) => cam.name === "Cam_orbit_01EXT");

  const defaultVariants: string[] = variant
    ? [
        configuration.modelDefaults[variant.code].colorId,
        configuration.modelDefaults[variant.code].friendly_name,
      ]
    : [];

  const updateIngloPlatformEnvironment = async () => {
    if (wasmMethods && "loadEnvironmentLevels" in wasmMethods) {
      await wasmMethods.loadEnvironmentLevels(
        JSON.stringify(
          getEnvLoadLevelsObject({
            environmentId: scenarios[1]?.id,
            cameraId: exteriorMainCamera?.id,
            dayTime: scenarios[1]?.sun_state.day_time,
            northyaw: scenarios[1]?.sun_state.north_yaw,
            noFade: true,
            applyDaytime: true,
            cameraData: exteriorMainCamera,
          }),
        ),
      );
    }
  };

  const updateEnvironment = async (isRebounceCamera = true) => {
    const { selectedCameraView } = demoState;
    if (wasmMethods && "loadEnvironmentLevels" in wasmMethods) {
      await wasmMethods.loadEnvironmentLevels(
        JSON.stringify(
          getEnvLoadLevelsObject({
            environmentId: scenarios[0]?.id,
            cameraId: selectedCameraView?.camId,
            dayTime: scenarios[0]?.sun_state.day_time,
            northyaw: scenarios[0]?.sun_state.north_yaw,
            noFade: true,
            applyDaytime: true,
            cameraData: selectedCameraView?.cameraData,
          }),
        ),
      );

      isRebounceCamera &&
        updatePixelStreamingWithVariants(
          /**
           * Commented for now as all variants not required at initial render
           */
          [demoState?.variants, defaultVariants].flat(),
          // [defaultVariants].flat(),
        );
    }
  };

  const updatePixelStreamingWithVariants = async (changedValue: string | string[]) => {
    try {
      if (wasmMethods && "switchVariants" in wasmMethods) {
        await wasmMethods?.switchVariants(...changedValue);
      }
    } catch (ex) {
      console.log(ex, " switch variant cordis error");
    }
  };

  const callStreamHandler = () => {
    checkSessionAvailability()
      .then((data) => {
        setStreamingState((prev) => ({
          ...prev,
          switchStream: false,
          isStreamStarted: true,
          callStreamingAvailableAPI: false,
          isStreamingAvailable: data?.status === 200,
          streamingNotAvailable: data?.status !== 200,
        }));

        setTurntableState((prev) => ({
          ...prev,
          isTurntable: data?.status !== 200,
        }));

        setStreamViewType((prev) => (data?.status === 200 ? STREAM_TYPES.STREAMING : prev));

        // updateEnvironment();
      })
      .catch((err) => {
        setTurntableState((prev) => ({
          ...prev,
          isTurntable: true,
        }));

        setStreamingState((prev) => ({
          ...prev,
          isStreamingAvailable: false,
          callStreamingAvailableAPI: true,
          isDontNotify: false,
        }));

        setStreamViewType(STREAM_TYPES.TURNTABLE);
      });
  };

  const resetAnimationsPanel = () => {
    setCachedState((prev) => ({
      ...prev,
      variantId: MODELS[variant.code]?.variantId,
      colorId: isBE6 ? "E0_BodyPaint_DG_Glo" : "E0_BodyPaint_TR",
      animations: {
        dayMode: true,
        doorsOpen: false,
        headlights: false,
        sunroofOpen: false,
      },
    }));

    setDemoState((prev) => ({
      ...prev,
      animationCommands: [],
      sunState: SUN_STATE[MODELS[variant.code].code].dayMode,
    }));

    setTurntableState((prev) => ({
      ...prev,
      selectedVariants: LAMPS_OFF_VARIANT_EXT,
    }));
  };

  const handleChange = (type: StreamTypes) => {
    if (type === streamViewType) return;

    window.gtag(
      "event",
      "stream_type_change",
      getGtagData("model", `Change stream type to ${type}`),
    );

    switch (type) {
      case STREAM_TYPES.STREAMING: {
        if (streamViewType === STREAM_TYPES.IN_GLO) {
          setStreamViewType(STREAM_TYPES.STREAMING);
          updateEnvironment();

          if (isTurntable) {
            callStreamHandler();
          }

          break;
        }

        callStreamHandler();

        handleStream(true);
        resetAnimationsPanel();
        break;
      }

      case STREAM_TYPES.TURNTABLE: {
        const isXEV9E = variant.code === MODELS["XEV 9E"].code;

        setDemoState((prev) => {
          const animationWithoutGoodbye = prev.animationCommands.filter(
            (ani) =>
              !ani.startsWith(`${ANIMATIONS[MODELS_NAMES.m110].goodByeHello.name}|seek|`) &&
              !ani.startsWith(`${ANIMATIONS[MODELS_NAMES.m310].goodByeHello.name}|seek|`),
          );

          const isLightoff = turntableState.selectedVariants.some((variant) =>
            LAMPS_OFF_VARIANT_EXT.includes(variant),
          );
          return {
            ...prev,
            animationCommands: [
              ...animationWithoutGoodbye,
              ...(isLightoff
                ? [`${ANIMATIONS[MODELS[variant.code].code].goodByeHello.name}|seek|100`]
                : []),
            ],
          };
        });

        setTurntableState((prev) => ({
          ...prev,
          callStreamingAvailableAPI: false,
          isTurntable: type === STREAM_TYPES.TURNTABLE,
          turntableCameraNamePrefix: prev.turntableCameraNamePrefix,
        }));

        setStreamViewType(STREAM_TYPES.TURNTABLE);

        // resetAnimationsPanel();
        break;
      }

      case STREAM_TYPES.IN_GLO: {
        const isAlreadyStreaming = streamViewType === STREAM_TYPES.STREAMING;

        setTurntableState((prev) => ({
          ...prev,
          turntableCameraNamePrefix: CAMERA_PREFIX_NAMES.EXT,
        }));

        handleLoadScreen(true);
        setTimeout(() => {
          handleLoadScreen(false);
        }, 10);

        if (isAlreadyStreaming) {
          setStreamViewType(STREAM_TYPES.IN_GLO);
          updateIngloPlatformEnvironment();
          break;
        } else {
          if (isTurntable) {
            setStreamViewType(STREAM_TYPES.IN_GLO);
            return;
          }
          checkSessionAvailability()
            .then((data) => {
              setStreamingState((prev) => ({
                ...prev,
                switchStream: false,
                isStreamStarted: true,
                callStreamingAvailableAPI: false,
                isStreamingAvailable: data?.status === 200,
                streamingNotAvailable: data?.status !== 200,
              }));

              setTurntableState((prev) => ({
                ...prev,
                isTurntable: data?.status !== 200,
              }));

              setStreamViewType((prev) => (data?.status === 200 ? STREAM_TYPES.IN_GLO : prev));
            })
            .catch((err) => {
              setTurntableState((prev) => ({
                ...prev,
                isTurntable: true,
              }));

              setStreamingState((prev) => ({
                ...prev,
                isStreamingAvailable: false,
                callStreamingAvailableAPI: true,
                isDontNotify: false,
              }));

              setStreamViewType(STREAM_TYPES.TURNTABLE);
            });

          handleStream(true);
        }

        resetAnimationsPanel();
        break;
      }
      default: {
        console.warn("Incorrect type selected!");
      }
    }
  };

  const theme = useTheme();
  const isSmallport = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack direction="row" columnGap={{ xs: isMobile ? 0 : 1, sm: 1.5, md: 2 }}>
      <ViewChangeTitle
        variant="collapsible"
        isMobile={isMobile}
        isDraweOpenOnLg={isDraweOpenOnLg}
        selected={streamViewType === STREAM_TYPES.IN_GLO}
        onClick={() => handleChange(STREAM_TYPES.IN_GLO)}
        isDrawerOpenOnMd={isDrawerOpenOnMd}
      >
        {isSmallport ? "Inglo" : "Inglo Platform"}
      </ViewChangeTitle>

      <ViewChangeTitle
        variant="collapsible"
        isDraweOpenOnLg={isDraweOpenOnLg}
        isMobile={isMobile}
        selected={streamViewType === STREAM_TYPES.STREAMING}
        onClick={() => handleChange(STREAM_TYPES.STREAMING)}
        isDrawerOpenOnMd={isDrawerOpenOnMd}
      >
        Streaming
      </ViewChangeTitle>
      <ViewChangeTitle
        variant="collapsible"
        isDraweOpenOnLg={isDraweOpenOnLg}
        isMobile={isMobile}
        selected={streamViewType === STREAM_TYPES.TURNTABLE}
        onClick={() => {
          closeDrawer();
          handleChange(STREAM_TYPES.TURNTABLE);
        }}
        isDrawerOpenOnMd={isDrawerOpenOnMd}
      >
        Turntable
      </ViewChangeTitle>
    </Stack>
  );
}

type BottomBarProps = {
  isMobile: boolean;
  drawerOpen: boolean;
  handleStream: (shouldStream: boolean) => void;
  closeDrawer: VoidFunction;
  handleLoadScreen;
};

export default function BottomBar({
  isMobile,
  drawerOpen,
  handleStream,
  closeDrawer,
  handleLoadScreen,
}: BottomBarProps) {
  const {
    cachedState: {
      view,
      animations: { dayMode },
    },
    setCachedState,
  } = useContext(DataContext);

  const {
    cameras,
    scenarios,
    demoState,
    setDemoState,
    setTurntableState,
    turntableState,
    streamViewType,
  } = useContext(StreamContext);
  const theme = useTheme();

  const isTab = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDraweOpenOnLg =
    (useMediaQuery(theme.breakpoints.between("md", "lg")) && drawerOpen) || isTab;

  const isDrawerOpenOnMd = isTab && drawerOpen;

  const { isTurntable } = turntableState;

  const variant = useVariant();
  const getGtagData = useGtagVariantData();
  const { selectedCameraView } = demoState;

  const wasmMethods = getWasmMethods();

  const sortedInteriorCameras = cameras
    ?.filter((cam) => INTERIOR_CAMERA_NAMES.includes(cam.name))
    ?.sort((a, b) => INTERIOR_CAMERA_NAMES.indexOf(a.name) - INTERIOR_CAMERA_NAMES.indexOf(b.name));

  const interiorCameras: CameraViewType[] = sortedInteriorCameras?.map((cam, index) => ({
    id: `INT_Cam_${index + 1}`,
    label: INTERIOR_CAMERA_LABELS[index],
    default: index === 0,
    camId: cam.id,
    cameraData: cam,
  }));

  const sortedExteriorCameras = cameras
    ?.filter((cam) => EXTERIOR_CAMERA_NAMES.includes(cam.name))
    ?.sort((a, b) => EXTERIOR_CAMERA_NAMES.indexOf(a.name) - EXTERIOR_CAMERA_NAMES.indexOf(b.name));

  const exteriorCameras: CameraViewType[] = sortedExteriorCameras?.map((cam, index) => ({
    id: `EXT_Cam_${index + 1}`,
    label: EXTERIOR_CAMERA_LABELS[index],
    default: index === 0,
    camId: cam.id,
    cameraData: cam,
  }));

  const handleViewChange = async (newView: ViewTypes, cameraOption?: CameraViewType) => {
    if (streamViewType === STREAM_TYPES.IN_GLO) return;
    if (newView === view && (!cameraOption || cameraOption?.id === selectedCameraView?.id)) return;

    window.gtag(
      "event",
      "view_type_change",
      getGtagData(
        "model",
        `Change view type to ${newView}${cameraOption ? ` (${cameraOption.label})` : ""}`,
      ),
    );

    try {
      let camera: CameraViewType;
      const isInterior = newView === VIEW_TYPES.INTERIOR;
      const isExterior = newView === VIEW_TYPES.EXTERIOR;

      if (newView === VIEW_TYPES.INTERIOR) {
        camera = cameraOption ? cameraOption : interiorCameras[0];
      } else {
        camera = cameraOption ? cameraOption : exteriorCameras[0];
      }

      if (isInterior) {
        setTurntableState((prev) => ({
          ...prev,
          turntableCameraNamePrefix: CAMERA_PREFIX_NAMES.Driver,
        }));
      }

      if (isExterior) {
        setTurntableState((prev) => ({
          ...prev,
          turntableCameraNamePrefix: CAMERA_PREFIX_NAMES.EXT,
        }));
      }

      if (cameraOption) {
        setTurntableState((prev) => ({
          ...prev,
          turntableCameraNamePrefix: CAMERA_PREFIX_NAMES[cameraOption.label],
        }));
      }

      setDemoState((prev) => ({
        ...prev,
        selectedCameraView: camera,
        // animationCommands: prev.animationCommands.filter(
        //   (cmd) => !cmd.startsWith(`${ANIMATIONS[variant.code].sunroofBlind.name}|seek|`),
        // ),
      }));

      if (wasmMethods && "loadEnvironmentLevels" in wasmMethods && camera && !isTurntable) {
        handleLoadScreen(true);
        setTimeout(() => {
          handleLoadScreen(false, 500);
        }, 50);
        try {
          await wasmMethods.loadEnvironmentLevels(
            JSON.stringify(
              getEnvLoadLevelsObject({
                environmentId: scenarios[0]?.id,
                cameraId: camera?.camId,
                dayTime: dayMode
                  ? SUN_STATE[variant.code].dayMode.dayTime
                  : SUN_STATE[variant.code].nightMode.dayTime,
                northyaw: dayMode
                  ? SUN_STATE[variant.code].dayMode.northyaw
                  : SUN_STATE[variant.code].nightMode.northyaw,
                noFade: true,
                applyDaytime: true,
                cameraData: camera.cameraData,
              }),
            ),
          );
        } catch (ex) {
          console.error("cannot transform camera, due to some issue");
        }
      }
    } catch (error) {
      console.warn(`Error while changing view: '${newView}'`);
      return;
    }

    setCachedState((prev) => ({
      ...prev,
      view: newView,
    }));
  };

  const isIngloPlatform = streamViewType === STREAM_TYPES.IN_GLO;

  return (
    <StyledAppBar isMobile={isMobile} drawerOpen={isMobile ? false : drawerOpen}>
      <div style={{ visibility: isIngloPlatform ? "hidden" : "visible" }}>
        {interiorCameras.length > 1 && (
          <Collapse
            in={view === VIEW_TYPES.INTERIOR}
            sx={{ overflowX: "auto", overflowY: "hidden" }}
          >
            <Stack direction="row" spacing={1}>
              {interiorCameras.map((cam) => (
                <ViewChangeTitle
                  key={cam.id}
                  variant="collapsible"
                  isMobile={isMobile}
                  selected={selectedCameraView?.id === cam.id}
                  onClick={() => handleViewChange(VIEW_TYPES.INTERIOR, cam)}
                  sx={{ whiteSpace: "nowrap" }}
                  style={{ fontSize: "16px" }}
                >
                  {cam.label}
                </ViewChangeTitle>
              ))}
            </Stack>
          </Collapse>
        )}
      </div>

      {exteriorCameras.length > 1 && (
        <Collapse in={view === VIEW_TYPES.EXTERIOR} sx={{ overflowX: "auto", overflowY: "hidden" }}>
          <Stack direction="row" spacing={1}>
            {exteriorCameras.map((cam) => (
              <ViewChangeTitle
                key={cam.id}
                variant="collapsible"
                isMobile={isMobile}
                selected={selectedCameraView?.id === cam.id}
                onClick={() => handleViewChange(VIEW_TYPES.EXTERIOR, cam)}
                sx={{ whiteSpace: "nowrap" }}
                style={{ fontSize: "16px" }}
              >
                {cam.label}
              </ViewChangeTitle>
            ))}
          </Stack>
        </Collapse>
      )}

      <BaseContainer>
        <div style={{ visibility: isIngloPlatform ? "hidden" : "visible" }}>
          <ViewSelector
            isMobile={isMobile}
            streamViewType={streamViewType}
            selected={view}
            onChange={handleViewChange}
            isDraweOpenOnLg={isDraweOpenOnLg}
            isDrawerOpenOnMd={isDrawerOpenOnMd}
          />
        </div>

        <ModelSwitch
          isMobile={isMobile}
          handleStream={handleStream}
          closeDrawer={closeDrawer}
          handleLoadScreen={handleLoadScreen}
          isDraweOpenOnLg={isDraweOpenOnLg}
          isDrawerOpenOnMd={isDrawerOpenOnMd}
        />

        <EmptyDiv />
      </BaseContainer>
    </StyledAppBar>
  );
}

type StyledAppBarProps = {
  theme?: Theme;
  isMobile?: boolean;
  drawerOpen: boolean;
};

const StyledAppBar = styled("div")<StyledAppBarProps>`
  position: absolute;
  bottom: 0;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;

  width: 100%;
  background-color: ${({ theme, isMobile }) =>
    isMobile ? (isDarkTheme(theme?.palette.mode) ? "#101014" : "#fff") : "transparent"};
  transition-property: width, background-color;
  transition-duration: 0.3s;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: 0.5rem 0.25rem;
    row-gap: 0.25rem;
  }
`;

const BaseContainer = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EmptyDiv = styled("div")`
  display: block;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    display: none;
  }
`;

type ViewChangeTitleProps = TypographyProps & {
  theme?: Theme;
  disabled?: boolean;
  isMobile?: boolean;
  selected?: boolean;
  isDraweOpenOnLg?: boolean;
  isDrawerOpenOnMd?: boolean;
};

const ViewChangeTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "isMobile" && prop !== "disabled",
})<ViewChangeTitleProps>(
  ({ theme, disabled, isMobile, selected, isDraweOpenOnLg, isDrawerOpenOnMd }) => `
  font-size: ${isDrawerOpenOnMd ? "14px" : isDraweOpenOnLg ? "16px" : "19px"};
  color: ${isMobile ? theme.palette.text.primary : isDarkTheme(theme.palette.mode) ? theme.palette.common.black : theme.palette.common.white};

  font-optical-sizing: auto;
  font-style: normal;
  font-weight: ${selected ? 700 : 400};
  font-variation-settings: "wdth" 112.5;
  line-height: 28px;
  cursor: ${disabled ? "not-allowed" : "pointer"};
  text-transform: uppercase;
  position: relative;
  transition: all 0.2s ease;
  user-select: none;
  filter: blur(${disabled ? "2px" : "0"});

  border-radius: 8px;
  padding: ${isDrawerOpenOnMd ? `0.2rem 0.3rem` : `0.25rem 0.5rem`};
  background-image: linear-gradient(0deg, #${isMobile ? "transparent" : isDarkTheme(theme.palette.mode) ? "FFFFFF" : "000000"}4D, #${isMobile ? "transparent" : isDarkTheme(theme.palette.mode) ? "FFFFFF" : "000000"}4D);
  backdrop-filter: blur(4px);

  ${theme.breakpoints.down("sm")} {
    font-size: 14px !important;
    padding: 0.15rem 0.3rem;
    border-radius: 6px;
    filter: blur(${disabled ? "1.5px" : "0"});
  }

  &:after {
    content: "";
    height: ${selected ? "5px" : "0"};
    width: 25px;
    background-color: ${isMobile ? theme.palette.text.primary : isDarkTheme(theme.palette.mode) ? theme.palette.common.black : theme.palette.common.white};
    position: absolute;
    border-radius: 2px;
    bottom: -0.3em;
    left: 50%;
    transition: height 0.3s;
    transform: translate(-50%);

    ${theme.breakpoints.down("sm")} {
      height: ${selected ? "3px" : "0"};
      bottom: -0.2em;
    }
  }
`,
);
